import { storageBucket } from './consts';
import { addThumbnailPostfix } from './misc';

// Objects under teams/ and tournaments/ are world-readable, so their URL is a
// pure function of the path — no getDownloadURL round trip, no token, no cache.
// Filenames are unique per upload (see fileToResizedFile), which is what makes
// the URL safe to treat as immutable. users/ is private; see userImagePaths.
const storageBase = `https://firebasestorage.googleapis.com/v0/b/${storageBucket}/o`;

// The whole path is encoded, slashes included: the object name is one path
// segment of the REST URL, so its separators must arrive as %2F.
export const storageUrl = (path) =>
  `${storageBase}/${encodeURIComponent(path)}?alt=media`;

// users/ is readable only with a token, so no URL for it exists until someone
// asks for the bytes. Consumers get the path and resolve it via
// useStorageImage (see utils/storageImage).
export const userImagePaths = (id, image) =>
  image
    ? {
        imgPath: `users/${id}/${image}`,
        imgThumbPath: `users/${id}/${addThumbnailPostfix(image)}`,
      }
    : { imgPath: '', imgThumbPath: '' };

// Document image field → the two URLs every consumer reads. Empty strings when
// there is no image, so the `imgThumbUrl || placeholder` fallbacks keep working.
export const imageUrls = (folder, id, image) =>
  image
    ? {
        imgUrl: storageUrl(`${folder}/${id}/${image}`),
        imgThumbUrl: storageUrl(`${folder}/${id}/${addThumbnailPostfix(image)}`),
      }
    : { imgUrl: '', imgThumbUrl: '' };

// Drop the persisted download-URL cache this module used to keep — up to 500
// entries left in localStorage on every returning user's device.
try {
  localStorage.removeItem('imageUrls');
} catch {
  // Storage unavailable (Safari private mode) — nothing to clean up anyway
}

// Decode File/Blob into a drawable source: prefer ImageBitmap, fallback to <img>
async function decodeImage(file) {
  // Fast path if createImageBitmap is available
  const canUseBitmap = typeof createImageBitmap === 'function';

  if (canUseBitmap) {
    try {
      const bmp = await createImageBitmap(file);
      return { img: bmp, isBitmap: true, width: bmp.width, height: bmp.height };
    } catch {}
  }

  // Fallback: object URL + HTMLImageElement
  try {
    const url = URL.createObjectURL(file);
    const el = await loadHtmlImage(url);
    URL.revokeObjectURL(url);
    return {
      img: el,
      isBitmap: false,
      width: el.naturalWidth,
      height: el.naturalHeight,
    };
  } catch (e) {
    throw new Error('Failed to decode image', e);
  }
}

// Create and load an <img> asynchronously from a URL
function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.decoding = 'async';
    img.src = src;
  });
}

// Resize source to fit within maxDim, draw into a canvas, keep aspect ratio
function resizeToCanvas(source, srcW, srcH, maxDim = 2000) {
  const k = Math.min(1, maxDim / Math.max(srcW, srcH));
  const w = Math.max(1, Math.round(srcW * k));
  const h = Math.max(1, Math.round(srcH * k));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas 2D context not available');

  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

// Encode canvas to Base64
function canvasToBase64(canvas, mime = 'image/jpeg', quality = 0.82) {
  try {
    const dataUrl = canvas.toDataURL(mime, quality);
    const base64 = dataUrl.split(',')[1] || '';
    if (!base64) throw new Error('Empty base64');
    return base64;
  } catch (e) {
    throw new Error('Failed to encode image', e);
  } finally {
    canvas.width = 1;
    canvas.height = 1;
  }
}

// Build output filename with extension based on MIME. The timestamp prefix is
// what keeps image URLs cacheable forever: a re-upload lands under a new name,
// so the URL changes with it and a stale one cannot exist. It also stops two
// admins uploading the same IMG_1234.jpg from overwriting each other in temp/.
function buildNameReplaceExt(originalName, outMime) {
  const extMap = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
  };
  const newExt = extMap[outMime] || 'bin';
  const dot = originalName.lastIndexOf('.');
  const base = dot > 0 ? originalName.slice(0, dot) : originalName;
  return `${Date.now().toString(36)}-${base}.${newExt}`;
}

// Canvas → Blob (Promise)
function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Blob encode failed'));
        resolve(blob);
      },
      mime,
      quality
    );
  });
}

// File/Blob → base64 (resized)
export async function fileToBase64Image({
  file,
  maxDim = 2000,
  mime = 'image/jpeg',
  quality = 0.82,
}) {
  const { img, isBitmap, width, height } = await decodeImage(file);
  try {
    const canvas = resizeToCanvas(img, width, height, maxDim);
    const base64 = canvasToBase64(canvas, mime, quality);
    return base64;
  } finally {
    if (isBitmap) {
      try {
        img.close();
      } catch {}
    }
  }
}

// File/Blob → resized File
export async function fileToResizedFile({
  file,
  maxDim = 2000,
  mime = 'image/jpeg',
  quality = 0.82,
}) {
  const targetMime = mime || file.type || 'image/jpeg';
  const { img, isBitmap, width, height } = await decodeImage(file);

  try {
    const canvas = resizeToCanvas(img, width, height, maxDim); // decode → resize
    const blob = await canvasToBlob(canvas, targetMime, quality); // encode → Blob
    const outName = buildNameReplaceExt(file.name, blob.type);
    return new File([blob], outName, {
      type: blob.type,
      lastModified: Date.now(),
    });
  } finally {
    if (isBitmap) {
      try {
        img.close();
      } catch {}
    } // release ImageBitmap if used
  }
}
